import React from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Label,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

type GraphProps = {
  data: any;
  chartType: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  truncateXAxisLabels?: boolean;
};

type LegendItem = {
  label: string;
  fill: string;
  dataKey?: string;
};

const LISTING_CHART_MARGIN = { top: 14, right: 12, left: 8, bottom: 8 };
const DETAIL_CHART_MARGIN = { top: 16, right: 16, left: 12, bottom: 16 };

const renderTooltipContent = (
  label: string,
  payload: any[] | undefined
) => {
  const nonZeroItems = (payload || []).filter(
    (tooltipData: any) => Number(tooltipData.value) !== 0
  );

  if (!nonZeroItems.length) return null;

  return (
    <div className="hid-chart-tooltip">
      <h5 className="hid-chart-tooltip__heading">{label}</h5>
      <ul className="hid-chart-tooltip__list">
        {nonZeroItems.map((tooltipData: any, i: number) => (
          <li className="hid-chart-tooltip__list-item" key={i}>
            <span className="hid-chart-tooltip__list-label">
              {tooltipData.name}
            </span>
            <span>{tooltipData.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ChartLegend: React.FC<{ items: LegendItem[] }> = ({ items }) => {
  if (!items.length) return null;

  return (
    <ul className="graph-series-legend" aria-label="Chart legend">
      {items.map((item, index) => (
        <li className="graph-series-legend__item" key={item.dataKey || index}>
          <span
            className="graph-series-legend__swatch"
            style={{ backgroundColor: item.fill }}
          />
          <span className="graph-series-legend__label">{item.label}</span>
        </li>
      ))}
    </ul>
  );
};

const Graph: React.FC<GraphProps> = ({
  data,
  chartType,
  xAxisLabel,
  yAxisLabel,
  truncateXAxisLabels = false,
}) => {
  const isListing = truncateXAxisLabels;
  // Listing cards stay short (8); detail page shows a bit more (16)
  const xAxisLabelMaxLength = isListing ? 8 : 16;

  const truncateLabel = (label: string, max = xAxisLabelMaxLength) => {
    if (!label) {
      return { display: "", full: "" };
    }
    return label.length > max
      ? { display: `${label.slice(0, max)}…`, full: label }
      : { display: label, full: label };
  };

  const finalYAxisLabel = !yAxisLabel
    ? ""
    : yAxisLabel === "Count"
      ? yAxisLabel
      : `${yAxisLabel} Count`;
  const chartMargin = isListing ? LISTING_CHART_MARGIN : DETAIL_CHART_MARGIN;
  const tickFontSize = isListing ? 8 : 11;
  const xAxisHeight = isListing ? 56 : 110;
  const graphClassName = [
    chartType === "line" ? "area-chart" : "stacked-chart",
    "graph--vertical-x-labels",
    isListing ? "" : "graph--detail",
  ]
    .filter(Boolean)
    .join(" ");

  if (chartType === "line") {
    const seriesConfig: LegendItem[] = (data.AreaConfig || []).map(
      (area: any) => ({
        label: area.label,
        fill: area.fill,
        dataKey: area.dataKey,
      })
    );
    const processedData = (data.Data || []).map((d: any) => {
      const truncated = truncateLabel(d.key);
      return {
        ...d,
        key: truncated.display,
        fullKey: truncated.full,
      };
    });

    return (
      <div className={graphClassName}>
        <div className={`hid-chart ${isListing ? "listing-chart" : "detail-chart"}`}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={processedData} margin={chartMargin}>
              <CartesianGrid
                strokeDasharray="2 2"
                stroke="#D8D8D8"
                vertical={false}
              />
              <XAxis
                dataKey="key"
                interval={0}
                fontSize={tickFontSize}
                axisLine={false}
                tickLine={false}
                height={xAxisHeight}
                angle={-90}
                textAnchor="end"
                padding={{ left: 0, right: 0 }}
                tick={{ fill: "#6b7280", fontSize: tickFontSize }}
              >
                {!isListing && xAxisLabel ? (
                  <Label value={xAxisLabel} offset={0} position="insideBottom" dy={48} />
                ) : null}
              </XAxis>
              <YAxis
                fontSize={tickFontSize}
                axisLine={false}
                tickLine={false}
                width={isListing ? 28 : 70}
                tick={{ fill: "#6b7280" }}
              >
                {!isListing && finalYAxisLabel ? (
                  <Label
                    value={finalYAxisLabel}
                    angle={-90}
                    position="insideLeft"
                    style={{ textAnchor: "middle" }}
                  />
                ) : null}
              </YAxis>
              {seriesConfig.map((area, index) => (
                <Area
                  key={area.dataKey || index}
                  dataKey={area.dataKey}
                  name={area.label}
                  stackId="areamap"
                  fill={area.fill}
                  stroke={area.fill}
                  fillOpacity={0.35}
                  dot={{
                    strokeOpacity: 1,
                    strokeWidth: 2,
                    fill: "white",
                    r: isListing ? 4 : 5,
                    stroke: area.fill,
                  }}
                  activeDot={{ r: 6, stroke: "#00AEEF", strokeWidth: 2 }}
                />
              ))}
              <Tooltip
                cursor={false}
                content={(tooltipProps: any) => {
                  if (!tooltipProps?.active) return null;
                  const fullLabel =
                    tooltipProps.payload?.[0]?.payload?.fullKey ??
                    tooltipProps.label;
                  return renderTooltipContent(fullLabel, tooltipProps.payload);
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        {!isListing && <ChartLegend items={seriesConfig} />}
      </div>
    );
  }

  if (chartType === "bar") {
    const seriesConfig: LegendItem[] = (data.BarConfig || []).map(
      (bar: any) => ({
        label: bar.label,
        fill: bar.fill,
        dataKey: bar.dataKey,
      })
    );
    const processedData = (data.Data || []).map((d: any) => {
      const truncated = truncateLabel(d.name);
      return {
        ...d,
        name: truncated.display,
        fullName: truncated.full,
      };
    });

    return (
      <div className={graphClassName}>
        <div className={`hid-chart ${isListing ? "listing-chart" : "detail-chart"}`}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={processedData} margin={chartMargin}>
              <CartesianGrid
                strokeDasharray="2 2"
                stroke="#D8D8D8"
                vertical={false}
              />
              <XAxis
                dataKey="name"
                interval={0}
                fontSize={tickFontSize}
                axisLine={false}
                tickLine={false}
                height={xAxisHeight}
                angle={-90}
                textAnchor="end"
                padding={{ left: 0, right: 0 }}
                tick={{ fill: "#6b7280", fontSize: tickFontSize }}
              >
                {!isListing && xAxisLabel ? (
                  <Label value={xAxisLabel} offset={0} position="insideBottom" dy={48} />
                ) : null}
              </XAxis>
              <YAxis
                fontSize={tickFontSize}
                axisLine={false}
                tickLine={false}
                width={isListing ? 28 : 70}
                tick={{ fill: "#6b7280" }}
              >
                {!isListing && finalYAxisLabel ? (
                  <Label
                    value={finalYAxisLabel}
                    angle={-90}
                    position="insideLeft"
                    style={{ textAnchor: "middle" }}
                  />
                ) : null}
              </YAxis>
              {seriesConfig.map((bar, index) => (
                <Bar
                  key={bar.dataKey || index}
                  dataKey={bar.dataKey}
                  name={bar.label}
                  stackId="stackChart"
                  fill={bar.fill}
                  stroke="white"
                  strokeWidth={1}
                />
              ))}
              <Tooltip
                cursor={false}
                content={(tooltipProps: any) => {
                  if (!tooltipProps?.active) return null;
                  const fullLabel =
                    tooltipProps.payload?.[0]?.payload?.fullName ??
                    tooltipProps.label;
                  return renderTooltipContent(fullLabel, tooltipProps.payload);
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
        {!isListing && <ChartLegend items={seriesConfig} />}
      </div>
    );
  }

  return null;
};

export default Graph;
